import type { ReactNode } from 'react'

import type {
    IndexLiveSpec,
    IndexQuote,
    LiveSpec,
    MarketPhase,
    Quote,
    SessionInfo
} from '@/lib/market/types'

import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { getSession } from '@/lib/market/calendar'
import { indexQuoteAt, quoteAt } from '@/lib/market/live'

interface LiveState {
    session: SessionInfo | null
    progress: number
    phase: MarketPhase
    stale: boolean
    tick: number
}

interface LiveProviderProps {
    session: SessionInfo | null
    children: ReactNode
}

const LiveContext = createContext<LiveState>({
    session: null,
    progress: 1,
    phase: 'closed',
    stale: false,
    tick: 0
})

const UPDATE_INTERVAL = 5000

export const LiveProvider = ({ session, children }: LiveProviderProps): ReactNode => {
    const [state, setState] = useState<LiveState>({
        session,
        progress: session?.progress ?? 1,
        phase: session?.phase ?? 'closed',
        stale: false,
        tick: 0
    })

    useEffect(() => {
        if (!session) return

        const update = () => {
            const live = getSession(Date.now())
            const sameSession = live.day === session.day

            setState(previous => {
                const progress = sameSession ? live.progress : 1
                const stale = live.day > session.day

                if (previous.session === session && previous.progress === progress && previous.phase === live.phase && previous.stale === stale) return previous

                return { session, progress, phase: live.phase, stale, tick: previous.tick + 1 }
            })
        }

        update()
        const timer = window.setInterval(update, UPDATE_INTERVAL)

        return () => window.clearInterval(timer)
    }, [session])

    return (
        <LiveContext.Provider value={state}>
            {children}
        </LiveContext.Provider>
    )
}

export const useLive = (): LiveState => useContext(LiveContext)

export const useQuote = (spec: LiveSpec): Quote => {
    const { progress } = useLive()

    return useMemo(() => quoteAt(spec, progress), [spec, progress])
}

export const useQuotes = (specs: LiveSpec[]): Quote[] => {
    const { progress } = useLive()

    return useMemo(() => specs.map(spec => quoteAt(spec, progress)), [specs, progress])
}

export const useIndexQuote = (spec: IndexLiveSpec): IndexQuote => {
    const { progress } = useLive()

    return useMemo(() => indexQuoteAt(spec, progress), [spec, progress])
}

export const usePriceFlash = (value: number): string => {
    const previous = useRef(value)
    const [flash, setFlash] = useState('')

    useEffect(() => {
        if (value === previous.current) return

        const next = value > previous.current ? 'flash-up' : 'flash-down'
        previous.current = value
        setFlash(next)

        const timer = window.setTimeout(() => setFlash(''), 1100)

        return () => window.clearTimeout(timer)
    }, [value])

    return flash
}
